(() => {
  'use strict';

  const endpoint = 'https://navnlapp-personalizer-pilot-73szjswrildtg.azurewebsites.net/api/storefront/telemetry';
  const checkoutSelector = 'a[href*="/checkout"],button[name="checkout"],input[name="checkout"],[data-checkout]';
  const checkoutFallbackDelayMs = 1_500;
  let cartInspection;
  let checkoutFallbackTimer;
  let checkoutFallbackTarget;

  function correlationId() {
    try {
      let value = sessionStorage.getItem('navnlapp-telemetry-correlation');
      if (!value) {
        value = crypto.randomUUID?.() || `navnlapp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem('navnlapp-telemetry-correlation', value);
      }
      return value;
    } catch (error) {
      return `navnlapp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    }
  }

  function isDesignerSession() {
    try {
      return sessionStorage.getItem('navnlapp-telemetry-active') === '1';
    } catch (error) {
      return false;
    }
  }

  function send(event, productHandle = '', details = {}) {
    const body = JSON.stringify({
      event,
      correlationId: correlationId(),
      platform: 'shopify',
      productHandle,
      store: window.Shopify?.shop || location.hostname,
      details: {
        ...details,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        online: navigator.onLine,
        connectionType: navigator.connection?.effectiveType || '',
      },
    });
    fetch(endpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body,
    }).catch(() => {});
  }

  function errorDetails(value, location = '', line = 0) {
    const isError = value && typeof value === 'object';
    const name = isError && typeof value.name === 'string' ? value.name : typeof value;
    const message = isError && typeof value.message === 'string' ? value.message : String(value || 'Unknown error');
    let source = '';
    try {
      const url = new URL(location || window.location.href, window.location.href);
      source = `${url.origin}${url.pathname}`;
    } catch (error) {
      source = String(location || '').slice(0, 160);
    }
    return {
      errorName: name.slice(0, 80),
      errorMessage: message.replace(/[\r\n\t]+/g, ' ').slice(0, 240),
      errorLocation: source,
      errorLine: Number.isFinite(Number(line)) ? Math.max(0, Math.round(Number(line))) : 0,
    };
  }

  function isKnownStorefrontNoise(details) {
    const message = `${details.errorName || ''}: ${details.errorMessage || ''}`;
    return /No empty section markup found/i.test(message)
      || /^(?:TypeError:\s*)?(?:Failed to fetch|Load failed|Network request failed|NetworkError(?: when attempting to fetch resource\.?)?)$/i.test(message.trim())
      || /Error producing to the Monorail Edge/i.test(message)
      || (/Error completing request/i.test(message)
        && /network failure may have prevented/i.test(message)
        && /(?:Failed to fetch|NetworkError)/i.test(message))
      || (/Shopify\.customerPrivacy is undefined/i.test(message)
        && /marketingAllowed/i.test(message));
  }

  function reportClientError(reason, value, location = '', line = 0) {
    const details = errorDetails(value, location, line);
    if (isKnownStorefrontNoise(details)) return;
    send('client_error', '', { source: 'window', reason, stage: 'storefront', ...details });
  }

  function inspectCart() {
    if (cartInspection) return cartInspection;
    const root = window.Shopify?.routes?.root || '/';
    cartInspection = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => response.ok ? response.json() : null)
      .then((cart) => {
        const item = cart?.items?.find((candidate) => candidate?.properties && Object.prototype.hasOwnProperty.call(candidate.properties, '_navnlapp_design_id'));
        return item ? { hasDesign: true, productHandle: String(item.handle || '').slice(0, 120) } : { hasDesign: false, productHandle: '' };
      })
      .catch(() => ({ hasDesign: false, productHandle: '' }));
    return cartInspection;
  }

  function trackCheckout(target) {
    if (!target?.closest?.(checkoutSelector)) return;
    inspectCart().then((cart) => {
      if (!cart.hasDesign) return;
      try {
        const last = Number(sessionStorage.getItem('navnlapp-checkout-telemetry-at') || 0);
        if (Date.now() - last < 30_000) return;
        sessionStorage.setItem('navnlapp-checkout-telemetry-at', String(Date.now()));
      } catch (error) {
        // Duplicate protection is best effort only.
      }
      send('checkout_started', cart.productHandle, { source: 'shopify_cart', stage: 'checkout' });
    });
  }

  function checkoutPath() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/?$/, '/')}checkout`;
  }

  function clearCheckoutFallback() {
    if (checkoutFallbackTimer) window.clearTimeout(checkoutFallbackTimer);
    checkoutFallbackTimer = undefined;
    if (checkoutFallbackTarget) checkoutFallbackTarget.removeAttribute('aria-busy');
    checkoutFallbackTarget = undefined;
  }

  function armCheckoutFallback(event) {
    const target = event?.submitter || event?.target;
    const checkout = target?.closest?.(checkoutSelector);
    if (!checkout || !location.pathname.includes('/cart')) return;
    if (event?.type === 'click' && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    if (checkoutFallbackTimer) return;

    checkoutFallbackTarget = checkout;
    checkout.setAttribute('aria-busy', 'true');
    checkoutFallbackTimer = window.setTimeout(() => {
      checkoutFallbackTimer = undefined;
      if (!location.pathname.includes('/cart') || document.visibilityState === 'hidden') {
        clearCheckoutFallback();
        return;
      }
      inspectCart().then((cart) => {
        if (cart.hasDesign) {
          send('checkout_fallback', cart.productHandle, {
            source: 'shopify_cart',
            stage: 'checkout',
            reason: 'native_checkout_did_not_navigate',
          });
        }
      });
      window.location.assign(checkoutPath());
    }, checkoutFallbackDelayMs);
  }

  document.addEventListener('pointerdown', (event) => trackCheckout(event.target), true);
  document.addEventListener('click', armCheckoutFallback, true);
  document.addEventListener('submit', (event) => {
    trackCheckout(event.submitter || event.target);
    armCheckoutFallback(event);
  }, true);
  window.addEventListener('pagehide', clearCheckoutFallback);
  window.addEventListener('pageshow', clearCheckoutFallback);
  if (location.pathname.includes('/cart') || document.querySelector(checkoutSelector)) inspectCart();

  window.addEventListener('error', (event) => {
    if (isDesignerSession()) reportClientError('window_error', event.error || event.message, event.filename, event.lineno);
  });
  window.addEventListener('unhandledrejection', (event) => {
    if (isDesignerSession()) reportClientError('unhandled_rejection', event.reason);
  });
})();
